import { Controller } from "@hotwired/stimulus";
import Color from "colorjs.io";
import * as Sentry from "@sentry/browser";

export default class extends Controller {
  static targets = ["picker", "hidden"];
  static values = {
    default: String,
  };

  connect() {
    // On connect, we need to:
    // 1. If there's a value in the hidden field, use that
    // 2. Otherwise, use the default value
    const value = this.hiddenTarget.value || this.defaultValue;
    this.setPickerFromValue(value);
  }

  setPickerFromValue(value) {
    if (!value) return;

    try {
      // If the value starts with #, it's already a hex color
      if (value.startsWith("#")) {
        this.pickerTarget.value = value.toLowerCase();
        // Also update the hidden field with the OKLCH value
        this.updateHiddenFromHex(value);
        return;
      }

      // Otherwise, assume it's OKLCH coordinates
      const [l, c, h] = value.split(" ").map(Number);

      // Handle special cases
      if (l === 1 && c === 0 && h === 0) {
        this.pickerTarget.value = "#ffffff";
        return;
      }
      if (l === 0 && c === 0 && h === 0) {
        this.pickerTarget.value = "#000000";
        return;
      }

      // Convert OKLCH to hex
      const color = new Color("oklch", [l, c, h]);
      const hex = color.to("srgb").toString({ format: "hex" });
      this.pickerTarget.value = hex;
    } catch (e) {
      Sentry.captureException(e);
      this.pickerTarget.value = "#000000";
    }
  }

  updateHiddenFromHex(hex) {
    try {
      // Handle special cases
      if (hex.toLowerCase() === "#ffffff") {
        this.hiddenTarget.value = "1 0 0";
        return;
      }
      if (hex.toLowerCase() === "#000000") {
        this.hiddenTarget.value = "0 0 0";
        return;
      }

      // Convert hex to OKLCH
      const color = new Color(hex);
      const oklch = color.to("oklch");
      const [l, c, h] = oklch.coords;

      // Handle NaN in hue by defaulting to 0
      const sanitizedH = isNaN(h) ? 0 : h;

      // Format with reasonable precision to avoid floating point issues
      this.hiddenTarget.value = `${l.toFixed(6)} ${c.toFixed(6)} ${sanitizedH.toFixed(6)}`;
    } catch (e) {
      Sentry.captureException(e);
      this.hiddenTarget.value = "";
    }
  }

  colorChanged(event) {
    const hex = event.target.value;
    this.updateHiddenFromHex(hex);
  }

  reset() {
    // Clear the hidden value to indicate we're using the default
    this.hiddenTarget.value = "";

    // Set the picker to show the default value
    this.setPickerFromValue(this.defaultValue);
  }
}
